import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, LogOut, ShieldCheck, Store } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ModeSwitch from "@/components/simple/ModeSwitch";
import { accentPair } from "@/lib/contrast";
import HelpPanel from "@/components/backoffice/HelpPanel";

const SHOP_TABS = [
    { key: "orders", label: "Orders", to: "/account/orders" },
    { key: "images", label: "Images", to: "/account/orders?tab=images" },
];
const SHOP_MENU = [
    { key: "products", label: "Products", to: "/account/products" },
    { key: "payouts", label: "Payouts", to: "/account/payouts" },
    { key: "branding", label: "Branding", to: "/account/store?tab=branding" },
    { key: "settings", label: "Settings", to: "/account/store?tab=settings" },
];
const ADMIN_TABS = [
    { key: "orders", label: "Orders", to: "/admin?tab=orders" },
    { key: "images", label: "Images", to: "/admin?tab=images" },
];
const ADMIN_MENU = [
    { key: "shops", label: "Shops", to: "/admin?tab=shops" },
    { key: "catalog", label: "Catalog", to: "/admin?tab=catalog" },
    { key: "fees", label: "Payouts & fees", to: "/admin?tab=fees" },
    { key: "flags", label: "Flags & mode", to: "/admin?tab=flags" },
];

const activeKey = (loc, platform) => {
    const q = new URLSearchParams(loc.search);
    const tab = q.get("tab") || "";
    if (platform) return tab || "orders";
    if (loc.pathname.startsWith("/account/orders") || loc.pathname === "/account") return tab === "images" ? "images" : "orders";
    if (loc.pathname.startsWith("/account/products")) return "products";
    if (loc.pathname.startsWith("/account/payout")) return "payouts";
    if (loc.pathname.startsWith("/account/store") || loc.pathname.startsWith("/account/domain")) return tab === "settings" ? "settings" : "branding";
    return "";
};

function Item({ item, active, big = false, testid }) {
    return (
        <Link to={item.to} data-testid={testid} aria-current={active ? "page" : undefined}
            className={`inline-flex min-h-[44px] shrink-0 items-center border-b-2 px-3 font-semibold ${big ? "text-[18px]" : "text-[16px]"} ${active
                ? "border-[var(--accent)] text-[#1a1a1a]" : "border-transparent text-[#4d4d4d]"}`}>
            {item.label}
        </Link>
    );
}

/** 8.1 — the one back-office shell: logo · Orders | Images · menu row · View my shop · Simple | Classic. No hamburger, no nesting. */
export default function BoShell({ store, platform = false, children, onSignOut }) {
    const { isAdmin } = useAuth();
    const loc = useLocation();
    const navigate = useNavigate();
    const [help, setHelp] = useState(false);
    const active = activeKey(loc, platform);
    const tabs = platform ? ADMIN_TABS : SHOP_TABS;
    const menu = platform ? ADMIN_MENU : SHOP_MENU;
    const { accent, on } = accentPair(platform ? "#1a1a1a" : store?.theme?.primary);
    useEffect(() => { setHelp(false); }, [loc.pathname, loc.search]);
    const shopUrl = store?.slug ? `/stores/${store.slug}` : "/";
    const bare = !platform && !store;   // a shopper with no shop: just the account chrome

    return (
        <div className="bo-simple min-h-screen bg-white text-[#1a1a1a]" style={{ "--accent": accent, "--on-accent": on, "--shop-primary": accent }} data-testid="bo-simple-shell">
            <header className="border-b border-[#1a1a1a]" data-testid="bo-simple-header">
                <div className="mx-auto flex max-w-[90rem] flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
                    <Link to={platform ? "/admin" : "/account"} className="flex min-h-[44px] items-center gap-2" data-testid="bo-logo">
                        {!platform && store?.logo && <img src={mediaUrl(store.logo)} alt="" className="h-9 w-9 object-contain" />}
                        <span className="text-[18px] font-semibold" data-testid="bo-shop-name">{platform ? "13thav Platform" : (store?.name || "My account")}</span>
                    </Link>
                    {!bare && (
                        <nav className="order-3 flex w-full items-center gap-1 sm:order-none sm:w-auto" aria-label="Orders and images" data-testid="bo-tabs">
                            {tabs.map((t) => <Item key={t.key} item={t} active={active === t.key} big testid={`bo-tab-${t.key}`} />)}
                        </nav>
                    )}
                    {!bare && (
                        <nav className="order-4 flex w-full items-center gap-1 overflow-x-auto sm:order-none sm:w-auto sm:flex-1" aria-label="Menu" data-testid="bo-menu-row">
                            {menu.map((m) => <Item key={m.key} item={m} active={active === m.key} testid={`bo-menu-${m.key}`} />)}
                            <button onClick={() => setHelp(true)} data-testid="bo-menu-help"
                                className="inline-flex min-h-[44px] shrink-0 items-center border-b-2 border-transparent px-3 text-[16px] font-semibold text-[#4d4d4d]">Help</button>
                        </nav>
                    )}
                    <div className="ml-auto flex flex-wrap items-center justify-end gap-x-2 gap-y-1">
                        {!platform && store?.slug && (
                            <Link to={shopUrl} data-testid="bo-view-shop" className="inline-flex min-h-[44px] items-center gap-1 whitespace-nowrap text-[15px] font-medium underline">
                                View my shop <ArrowUpRight size={14} />
                            </Link>
                        )}
                        <ModeSwitch scope={platform || isAdmin ? "platform" : "shop"} override={store?.site_mode} light />
                        {isAdmin && (
                            <Link to={platform ? "/account" : "/admin"} data-testid={platform ? "bo-to-shop" : "bo-to-admin"}
                                className="inline-flex min-h-[44px] items-center gap-1 border border-[#1a1a1a] px-2 text-[14px] font-semibold">
                                {platform ? <><Store size={14} /> My shop</> : <><ShieldCheck size={14} /> Platform admin</>}
                            </Link>
                        )}
                        <button onClick={onSignOut || (() => navigate("/"))} data-testid="bo-sign-out"
                            className="inline-flex min-h-[44px] items-center gap-1 whitespace-nowrap text-[14px] font-medium underline"><LogOut size={14} /> Sign out</button>
                    </div>
                </div>
            </header>
            <main className="mx-auto max-w-[90rem] px-3 py-4" data-testid="bo-main">{children}</main>
            {help && <HelpPanel onClose={() => setHelp(false)} platform={platform} />}
        </div>
    );
}
